import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const GoogleAuthContext = createContext({
  user: null,
  isReady: false,
  clientIdPresent: false,
  login: () => {},
  logout: () => {},
});

export default GoogleAuthContext;

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const STORAGE_KEY = "gcc-google-user";

const decodeCredential = (credential) => {
  const payload = credential.split(".")[1];
  const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join("")
  );
  return JSON.parse(jsonPayload);
};

export function GoogleAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const initializedRef = useRef(false);

  const handleCredentialResponse = useCallback((response) => {
    if (!response?.credential) return;
    try {
      const payload = decodeCredential(response.credential);
      if (!payload?.email) return;
      if (payload.email_verified === false) return;
      const userInfo = {
        email: payload.email,
        name: payload.name || payload.email,
        picture: payload.picture || "",
      };
      setUser(userInfo);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(userInfo));
    } catch (error) {
      console.error("Failed to decode Google credential", error);
    }
  }, []);

  useEffect(() => {
    const storedUser = sessionStorage.getItem(STORAGE_KEY);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  const handleScriptLoad = useCallback(() => {
    setScriptLoaded(true);
  }, []);

  useEffect(() => {
    if (!CLIENT_ID) return;

    if (window.google?.accounts?.id) {
      setScriptLoaded(true);
      return;
    }

    const existingScript = document.getElementById("google-identity-script");
    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad, { once: true });
      return () =>
        existingScript.removeEventListener("load", handleScriptLoad);
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.id = "google-identity-script";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", handleScriptLoad);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener("load", handleScriptLoad);
    };
  }, [handleScriptLoad, CLIENT_ID]);

  useEffect(() => {
    if (
      !CLIENT_ID ||
      !scriptLoaded ||
      initializedRef.current ||
      !window.google?.accounts?.id
    ) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: CLIENT_ID,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    initializedRef.current = true;
    setIsReady(true);
  }, [scriptLoaded, handleCredentialResponse, CLIENT_ID]);

  const login = useCallback(() => {
    if (initializedRef.current && window.google?.accounts?.id) {
      window.google.accounts.id.prompt();
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    sessionStorage.removeItem(STORAGE_KEY);
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  }, []);

  const value = useMemo(() => ({
    user,
    isReady,
    clientIdPresent: Boolean(CLIENT_ID),
    login,
    logout,
  }), [user, isReady, login, logout, CLIENT_ID]);

  return (
    <GoogleAuthContext.Provider value={value}>
      {children}
    </GoogleAuthContext.Provider>
  );
}
